import {Fragment, useEffect, useState} from "react";
import {PriceDisplay} from "../controller/CreateOrder";
import {DataTable} from "./datatable/DataTable";
import {formatPrice, parseLocaleNumber} from "../js/util";
import {useHistory} from "react-router";
import {ReactComponent as ForwardButton} from "../img/icon/back-arrow.svg";
import {getGlobal, globalKeys, showError} from "../js/main";
import {get, post} from "../js/api";
import {InputTypeCell} from "./InputTypeCell";
import {S3Attachment} from "./S3Attachment";
import {SelectReact} from "./form/SelectReact";
import {AttachmentList} from "./AttachmentList";
import {createKeySourceIdentifier, getKeyIdFromSourceIdentifier, isKeySourceIdentifier} from "../js/orderItemSource";

const MIN_ITEM_AMOUNT = 1;

const normalizeAmount = (amount) => {
    const parsed = parseInt(amount, 10);
    return Number.isNaN(parsed) ? MIN_ITEM_AMOUNT : Math.max(MIN_ITEM_AMOUNT, parsed);
}

export const OrderItems = ({data, inline = false, updateOrderFunction = null, onItemAmountChange = null}) => {

    const [availableKeys, setAvailableKeys] = useState([]);
    const canEditAmount = !!updateOrderFunction || !!onItemAmountChange;
    const isPublicOrder = !data.username;

    useEffect(() => {
        if(updateOrderFunction){
            get("/keys").then((keys) => setAvailableKeys(keys.map(k => {
                return {value: k.id, label: `${k.title} (${formatPrice(k.price)})`, price: k.price, sourceIdentifier: createKeySourceIdentifier(k.id)}
            })));
        }
    }, [updateOrderFunction]);

    const updateRow = (row, changes) => {
        const newOrder = {...data}
        newOrder.items = [...data.items];
        newOrder.items[data.items.indexOf(row)] = {...row, ...changes};
        updateOrderFunction(newOrder);
    }

    const fieldEntered = (row, field, newValue) => {
        updateRow(row, {[field]: newValue});
    }

    const itemChangesFromDiscountInfo = (discountInfo) => ({
        amount: discountInfo.amount,
        price: discountInfo.price,
        discount: discountInfo.discount,
        sourceIdentifier: discountInfo.sourceIdentifier
    });

    const discountedOrderItem = (row, amount, keyId, applyDiscountInfo = null) => {
        post(`/public/keys/${keyId}/discountOrderItem`, {orderItemId: row.id, amount}, false)
            .then(discountInfo => {
                if (applyDiscountInfo) {
                    applyDiscountInfo(discountInfo);
                } else {
                    updateRow(row, itemChangesFromDiscountInfo(discountInfo));
                }
            })
            .catch(() => showError("Rabatt konnte nicht berechnet werden."));
    }

    const amountChanged = (row, amount) => {
        const normalizedAmount = normalizeAmount(amount);
        const keyId = getKeyIdFromSourceIdentifier(row.sourceIdentifier);

        if (onItemAmountChange) {
            if (isPublicOrder && keyId) {
                discountedOrderItem(row, normalizedAmount, keyId, discountInfo => onItemAmountChange(row, itemChangesFromDiscountInfo(discountInfo)));
            } else {
                onItemAmountChange(row, normalizedAmount);
            }
            return;
        }

        if (isPublicOrder && keyId) {
            discountedOrderItem(row, normalizedAmount, keyId);
            return;
        }

        fieldEntered(row, 'amount', normalizedAmount);
    }

    const canEditRowAmount = (row) => !!updateOrderFunction || (!!onItemAmountChange && isPublicOrder && isKeySourceIdentifier(row.sourceIdentifier));

    const augmentWithPriceRow = (row) => {
        if (!row.price || availableKeys.some(k => k.sourceIdentifier === row.sourceIdentifier)) {
            return availableKeys;
        }
        return [...availableKeys, {value: 'row#' + row.id, label: `${formatPrice(row.price)}`, price: row.price}]
    }

    const selectedPriceValue = (row) => {
        const keyId = getKeyIdFromSourceIdentifier(row.sourceIdentifier);
        return keyId ? Number(keyId) : 'row#' + row.id;
    }

    const keySelected = (row, value) => {
        const selectedKey = availableKeys.find(k => String(k.value) === String(value));
        if (selectedKey) {
            if (isPublicOrder) {
                discountedOrderItem(row, normalizeAmount(row.amount), selectedKey.value);
            } else {
                updateRow(row, {price: selectedKey.price, sourceIdentifier: selectedKey.sourceIdentifier});
            }
            return;
        }

        const selectedPrice = augmentWithPriceRow(row).find(k => k.value === value);
        if (selectedPrice) {
            updateRow(row, {price: selectedPrice.price, sourceIdentifier: null});
        }
    }

    const createOption = (row, price) => {
        const parsed = parseLocaleNumber(price);
        if (isNaN(parsed)) {
            showError("Preiseingabe ungültig: " + price);
            return;
        }
        updateRow(row, {price: parsed, sourceIdentifier: null});
    }

    const itemColumns = [
        {
            name: "Artikel",
            cell: (row) => <div>
                {updateOrderFunction &&
                    <InputTypeCell initialValue={row.title} save={newValue => fieldEntered(row, 'title', newValue)}
                                   type={"text"} required={true}></InputTypeCell>}
                {!updateOrderFunction && <h3 className="mt-05 mb-03">{row.title}</h3>}
                <div className="smaller">
                    {row.properties.map(prop => {
                        return prop.value &&
                            <Fragment key={prop.key}>
                                <span className={prop.key === "Systemmeldung" ? 'red' : ''}
                                      key={prop.key}>{prop.key}: <strong>{prop.value}</strong></span>
                                <br className="mb-03"/>
                            </Fragment>
                    })}
                    {row.attachments.length > 0 && <Fragment>
                        <span><strong>Anhänge: <br/></strong>{row.attachments.map(att => {
                            return <S3Attachment key={att.s3identifier} s3identifier={att.s3identifier}
                                                 signedUrl={att.signedUrl}/>
                        })}</span>
                    </Fragment>}
                    <br/>
                </div>
            </div>
        },
        {
            name: "Anzahl",
            maxWidth: '150px',
            right: true,
            cell: (row) => <div>
                {canEditAmount &&
                    canEditRowAmount(row) &&
                    <AmountStepper value={row.amount} onChange={amount => amountChanged(row, amount)}/>}
                {!canEditRowAmount(row) && <span>{row.amount}</span>}
            </div>
        },
        {
            name: "Stückpreis",
            maxWidth: '350px',
            style: {'width': '100%'},
            right: true,
            cell: (row) => <Fragment>
                {updateOrderFunction &&
                    <SelectReact name="price" value={selectedPriceValue(row)} containerStyle={{"width": "100%"}} required
                                 menuPortalTarget={document.body}
                                 options={augmentWithPriceRow(row)} setter={newValue => keySelected(row, newValue)}
                                 canAddNew={true}
                                 formatCreateLabel={(val) => val + " €"}
                                 onCreateOption={value => createOption(row, value)}
                                 placeholder={"Schüssel suchen / Preis eingeben"}/>}
                {!updateOrderFunction && <span key={row.id}>{formatPrice(row.price)}</span>}
            </Fragment>
        },
        ...(updateOrderFunction ? [{
            name: "Rabatt %",
            maxWidth: '200px',
            right: true,
            cell: (row) => <Fragment>
                {updateOrderFunction && <InputTypeCell initialValue={row.discount}
                                                       save={newValue => updateRow(row, {discount: +newValue, sourceIdentifier: null})}
                                                       type={"number"} required={true}/>}
                {!updateOrderFunction && <span key={row.id}>{row.discount}</span>}
            </Fragment>
        }] : []),
        {
            name: "Preis (netto)",
            maxWidth: '350px',
            right: true,
            selector: (row) => PriceDisplay(row.price, row.discount, row.amount)
        }
    ];

    const history = useHistory();

    const openOrder = (data) => {
        if (getGlobal(globalKeys.isAdmin)) {
            history.push("/bestellungen/bearbeiten/" + data.id);
        } else {
            history.push("/meine-bestellungen/bearbeiten/" + data.id);
        }
    };

    const readonly = !updateOrderFunction;

    return (
        <div className={`order-items ${readonly ? "order-items-readonly" : "order-items-updateable"}`}>
            <br/>
            {inline &&
                <div className="center">
                    <button className="forward-button mb-1" onClick={() => openOrder(data)}>
                        <ForwardButton/>
                        Bestelldetails anzeigen
                    </button>
                </div>}

            <div className={'data-table no-hover order-items-table-view mt-0 ' + (inline ? 'mb-2' : 'mb-04')}>
                <DataTable data={data.items}
                           columns={itemColumns}
                           noHeader
                           noSearch
                           noScrollInfo
                           removeTopPadding/>
            </div>
            {readonly && <div className="order-item-card-list">
                {data.items.map((row, index) => <OrderItemCard key={row.id ?? index}
                                                               row={row}
                                                               onAmountChange={onItemAmountChange && isPublicOrder && isKeySourceIdentifier(row.sourceIdentifier) ? amount => amountChanged(row, amount) : null}/>)}
            </div>}
        </div>
    );

}

const AmountStepper = ({value, onChange}) => {
    const amount = normalizeAmount(value);

    return <div className="amount-stepper">
        <button type="button"
                aria-label="Anzahl verringern"
                disabled={amount <= MIN_ITEM_AMOUNT}
                onClick={() => onChange(amount - 1)}>-</button>
        <span>{amount}</span>
        <button type="button"
                aria-label="Anzahl erhöhen"
                onClick={() => onChange(amount + 1)}>+</button>
    </div>
}

const OrderItemCard = ({row, onAmountChange = null}) => {
    return <article className="order-item-card">
        <header className="order-item-card-header">
            <h3>{row.title}</h3>
            <span>{PriceDisplay(row.price, row.discount, row.amount)}</span>
        </header>
        <div className="order-item-card-content">
            <dl className="order-item-card-properties">
                {row.properties.map(prop => prop.value &&
                    <Fragment key={prop.key}>
                        <dt className={prop.key === "Systemmeldung" ? 'red' : ''}>{prop.key}</dt>
                        <dd>{prop.value}</dd>
                    </Fragment>)}
                <dt>Anzahl</dt>
                <dd>
                    {onAmountChange &&
                        <AmountStepper value={row.amount} onChange={onAmountChange}/>}
                    {!onAmountChange && row.amount}
                </dd>
                <dt>Stückpreis</dt>
                <dd>{formatPrice(row.price)}</dd>
                {(row.discount || 0) !== 0 && <>
                    <dt>Rabatt</dt>
                    <dd>{row.discount} %</dd>
                </>}
            </dl>
            <AttachmentList attachments={row.attachments}
                            compact
                            defaultOpen={false}/>
        </div>
    </article>;
}
